import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { findStepByHash, stepsForPath } from '@/shared/walkthrough-steps';
import { usePersona, useWalkthrough } from '@/app/providers';
import { useDemoPath } from '@/versions/v1/chrome/Walkthrough';

// useWalkthroughSync — equivalent of the source mock's `onHashChange` +
// persona auto-flip logic, ported to React Router. On every route change:
//   1. resolve the current path+search to a step (exact match only)
//   2. if the path matches a step, auto-flip the persona to step.persona
//   3. update the walkthrough's currentStep and pathId to keep the indicator in sync
//
// While `freeExplore` is on, a URL that still matches a step keeps the
// indicator and persona aligned (paused-at-step read) without leaving free explore.
//
// 2026-05-17 audit D1 gate: the hook is a no-op when the walkthrough is
// gated off (no demo path param). Default users get no persona auto-flip and
// no freeExplore writes — the walkthrough is invisible end-to-end.
export function useWalkthroughSync() {
  const demoPath = useDemoPath();
  const location = useLocation();
  const { setPersona } = usePersona();
  const { freeExplore, setCurrentStep, setFreeExplore, setPathId } = useWalkthrough();
  const lastPath = useRef<string>('');

  useEffect(() => {
    if (!demoPath) return;
    const here = location.pathname + (location.search ?? '');
    if (here === lastPath.current) return;
    lastPath.current = here;

    setPathId(demoPath);
    const spine = stepsForPath(demoPath);
    const match = findStepByHash(here);
    if (match) {
      setCurrentStep(match.index);
      setPersona(match.path.steps[match.index]!.persona);
      return;
    }

    setCurrentStep(0);
    setPersona(spine[0]!.persona);

    if (freeExplore) return;

    // User wandered off-spine — pause the walkthrough into Free Explore
    // rather than fighting back with a redirect.
    setFreeExplore(true);
  }, [
    demoPath,
    location.pathname,
    location.search,
    freeExplore,
    setCurrentStep,
    setFreeExplore,
    setPathId,
    setPersona,
  ]);
}
